import React, { useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Grid, Typography, CircularProgress } from '@material-ui/core';
import PathSVG from 'assets/images/svg/localization/PathSVG';
import InputText from 'components/inputs/InputText';
import LDBasePortalVirtualize from 'components/selects/virtualize/LDBasePortalVirtualize';
import Search from 'components/search';
import Checkbox from 'components/checkbox/Base';
import ListSelect from 'components/list/Select';
import ButtonBase from 'components/button/Base';
import * as gridActions from '../actions';
import { useDispatch } from 'react-redux';
import { SPACING_LIST_NAME_WITH_ICON, INPUT_HEIGHT } from 'const/style';
import { useParams, useHistory } from 'react-router-dom';
import { useLanguageOptionsWithUsageValidate } from 'hooks/app';
import { sendManualTrack } from 'tracker';
import produce from 'immer';
import ColorAddSVG from 'assets/images/svg/ColorAddSVG';
import DeleteSVG from 'assets/images/svg/DeleteSVG';
import { LOCALIZATION_TYPES, RECORD_IDENTIFIER_TYPE } from 'const';
import Tooltip from 'components/tooltip/Base';
import { AutoSizer, List } from 'react-virtualized-dn';
import chunk from 'lodash/chunk';
import NoDiffCheck from 'assets/images/svg/versionControl/NoDiffCheck';
import DialogContent from 'components/dialog/DialogContent';
import DialogTitle from 'components/dialog/DialogTitle';
import DialogActions from 'components/dialog/DialogActions';
import IOSSwitch from 'components/switches/IOS';
import { useTranslation } from 'react-i18next';
import * as gridUIActions from 'gridUI/actions';
import * as columnTypes from 'const/columnTypes';
import NormalEmptySetup from './NormalEmptySetup';
import { getGridRandomColor } from 'utils/color';
import QuestionSVG from 'assets/images/svg/QuestionSVG';
import AccessControl from 'auth/AccessControl';
import * as roleConst from 'auth/roleConst';
import { generateColumnMetaDataByHeaders } from 'utils/gridUI/formatData';
import { TRANSLATION_TYPES } from 'gridUI/column-types';

const useStyles = makeStyles(theme => ({
    root: {
        width: 740
    },
    search: {
        width: 240,
        backgroundColor: '#fff'
    },
    targetList: {
        padding: `${theme.spacing(3)}px ${theme.spacing(2)}px`,
        height: 150,
        overflowY: 'auto',
        borderRadius: 4,
        border: `1px solid ${theme.colors.border}`
    },
    originLanguage: {
        position: 'relative'
    },
    arrow: {
        position: 'absolute',
        right: -18,
        top: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    btnSubmit: {
        width: 120,
        position: 'relative',
        '& button svg': {
            marginLeft: SPACING_LIST_NAME_WITH_ICON
        }
    },
    buttonProgress: {
        color: theme.palette.primary.main,
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12
    },
    formControl: {
        '&.MuiFormControlLabel-root': {
            marginRight: 0
        }
    },
    spacing: {
        marginBottom: theme.spacing(3)
    },
    spacing2: {
        marginBottom: theme.spacing(2)
    },
    spacing4: {
        marginBottom: theme.spacing(4)
    },
    box: {
        padding: theme.spacing(3),
        border: `1px solid ${theme.colors.border}`,
        borderRadius: 4,
        overflowX: 'auto',
        backgroundColor: '#fff'
    },
    w170: {
        minWidth: 170
    },
    split: {
        marginLeft: theme.spacing(3),
        marginRight: theme.spacing(3)
    },
    fullWidth: {
        width: '100%'
    },
    addMoreSource: {
        height: INPUT_HEIGHT,
        cursor: 'pointer',
        minWidth: 170,
        border: `1px dashed ${theme.colors.border}`,
        borderRadius: 4,
        '& p': {
            color: theme.colors.lightLavender
        },
        '& svg': {
            marginRight: theme.spacing(2)
        },
        '&:hover': {
            background: theme.colors.solitude
        }
    },
    dlFlex: {
        display: 'flex'
    },
    langSelect: {
        maxWidth: 300
    },
    rowItem: {
        minWidth: 140
    },
    spb3: {
        marginBottom: theme.spacing(3)
    },
    virtualizeList: {
        outline: 'none'
    },
    numberStep: {
        width: 16,
        minWidth: 16,
        height: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: theme.colors.lightLavender,
        color: theme.colors.white,
        borderRadius: '50%',
        fontSize: 10,
        marginRight: theme.spacing(1),
        position: 'relative'
    },
    multiStepExplain: {
        maxWidth: 250,
        padding: theme.spacing(2)
    },
    row: {
        backgroundColor: '#F7F8FA',
        borderRadius: 4,
        padding: 14,

        '&:not(:last-child)': {
            marginBottom: 8
        }
    },
    displayFlex: {
        display: 'flex'
    }
}));

const ITEMS_PER_ROW = 4;

function LocalizationEmptySetup({
    dbId,
    onClose,
    isRedirect = true,
    onGridCreated,
    isTranslationGrid = true,
    defaultName = '',
    headers = [],
    totalRecord = 5
}) {
    const { t } = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const { workspaceId } = useParams();
    const history = useHistory();
    const theme = useTheme();
    const langOptions = useLanguageOptionsWithUsageValidate();

    const [nonUnique, setNonUnique] = React.useState(false);
    const [globalUnique, setGlobalUnique] = React.useState(false);
    const checkboxRecordIdRef = useRef();
    const [name, setName] = React.useState(defaultName || t('grid_default_name'));
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const [filterValue, setFilterValue] = React.useState('');

    const [sources, setSources] = React.useState([null]);

    const [selectedTargetOptions, setSelectedTargetOptions] = React.useState({});
    const [isTranslationOn, setTranslationOn] = React.useState(isTranslationGrid);

    const [contentColumns, setContentColumns] = React.useState([
        {
            name: 'Additional information',
            id: 'Additional_information',
            publicId: 'Additional_information',
            columnId: 'Additional_information',
            type: columnTypes.MULTIPLE_LINES
        },
        {
            name: 'Character limit',
            id: 'Character_limit',
            publicId: 'Character_limit',
            columnId: 'Character_limit',
            type: columnTypes.NUMBER
        },
        {
            name: t('grid_default_col_1'),
            id: 'column1',
            publicId: 'column1',
            columnId: 'column1',
            type: columnTypes.MULTIPLE_LINES
        }
    ]);
    const [normalColumns, setNormalColumns] = React.useState([
        {
            name: t('grid_default_col_1'),
            id: 'column1',
            publicId: 'column1',
            columnId: 'column1',
            type: columnTypes.MULTIPLE_LINES
        },
        {
            name: t('grid_default_col_2'),
            id: 'column2',
            publicId: 'column2',
            columnId: 'column2',
            type: columnTypes.MULTIPLE_LINES
        }
    ]);

    const targetOptions = React.useMemo(() => {
        const selectedSources = sources?.map(source => source?.value);
        return langOptions
            ?.filter(option => !selectedSources.includes(option.value))
            .filter(option => {
                return option?.label?.toLowerCase()?.includes(filterValue?.toLowerCase());
            });
    }, [langOptions, filterValue, sources]);

    const handleNonUniqueChange = React.useCallback(
        e => {
            const nonUniqueChecked = e.target.checked;
            setNonUnique(nonUniqueChecked);
            if (nonUniqueChecked && globalUnique) {
                setGlobalUnique(false);
            }
        },
        [globalUnique]
    );

    const handleGlobalUniqueChange = React.useCallback(
        e => {
            const globalUniqueChecked = e.target.checked;
            setGlobalUnique(globalUniqueChecked);
            if (globalUniqueChecked && nonUnique) {
                setNonUnique(false);
            }
        },
        [nonUnique]
    );

    const handleMultiSourceChange = React.useCallback(
        ({ option, index }) => {
            let updateSources = produce(sources, draft => {
                draft[index] = option;
            });
            setSources(updateSources);
            const newSelectedTargetOptions = { ...selectedTargetOptions };
            if (newSelectedTargetOptions[option?.value]) {
                delete newSelectedTargetOptions[option?.value];
            }
            setSelectedTargetOptions(newSelectedTargetOptions);
        },
        [sources, selectedTargetOptions]
    );

    const handleMultiSourceRemove = React.useCallback(
        index => {
            let sourcesClone = [...sources];
            sourcesClone.splice(index, 1);
            setSources(sourcesClone);
        },
        [sources]
    );

    const handleTargetSelect = React.useCallback(
        option => {
            const targetValue = option.value;
            const checked = selectedTargetOptions[targetValue] || false;

            let newSelectedTargetOptions = {};

            newSelectedTargetOptions = {
                ...selectedTargetOptions,
                [targetValue]: !checked
            };
            setSelectedTargetOptions(newSelectedTargetOptions);
        },
        [selectedTargetOptions]
    );

    const isDuplicatedValue = (columns, prop, value) => {
        const found = columns.filter(col => col[prop] === value);

        return found && found.length > 1;
    };

    const validSelectedTargetOptions = React.useMemo(() => {
        const validSelected = [];
        Object.keys(selectedTargetOptions).forEach(key => {
            if (selectedTargetOptions[key]) {
                const validOption = langOptions.find(option => option.value === key);
                if (validOption) {
                    validSelected.push(validOption);
                }
            }
        });
        return validSelected;
    }, [selectedTargetOptions, langOptions]);

    const isValidNormalInput = React.useMemo(() => {
        let result = true;

        const columns = isTranslationOn ? contentColumns : normalColumns;

        columns.forEach(col => {
            if (
                isEmpty(col.name) ||
                isEmpty(col.id) ||
                isDuplicatedValue(columns, 'id', col.id) ||
                isDuplicatedValue(columns, 'name', col.name)
            ) {
                result = false;

                return result;
            }
        });
        return result;
    }, [isTranslationOn, contentColumns, normalColumns]);

    const isCreateValid = React.useMemo(() => {
        const isValidName = !!name;
        const isValidSourceRequest = sources?.filter(source => !!source)?.length;
        const isValidTargetLanguages = validSelectedTargetOptions.length;

        return (
            (isTranslationOn && isValidName && isValidSourceRequest && isValidTargetLanguages && isValidNormalInput) ||
            (!isTranslationOn && isValidName && isValidNormalInput)
        );
    }, [name, sources, isTranslationOn, isValidNormalInput, validSelectedTargetOptions]);

    React.useEffect(() => {
        if (headers?.length) {
            const columnMapping = generateColumnMetaDataByHeaders(headers);
            const keys = Object.keys(columnMapping);
            const isTranslationGrid = keys?.some(key => columnMapping?.[key]?.type === columnTypes.TRANSLATION);

            if (isTranslationGrid) {
                const source = keys?.find(key => columnMapping?.[key]?.languageType === TRANSLATION_TYPES.SOURCE_LANG);

                if (source) {
                    const sourceLang = langOptions?.find(option => {
                        return option?.value === columnMapping?.[source]?.group;
                    });
                    setSources([sourceLang]);
                }

                const targets = keys?.filter(
                    key => columnMapping?.[key]?.languageType === TRANSLATION_TYPES.TARGET_LANG
                );
                let selectTargets = {};

                for (const target of targets) {
                    selectTargets = {
                        ...selectTargets,
                        [columnMapping?.[target]?.group]: true
                    };
                }
                setSelectedTargetOptions(selectTargets);
            }

            const otherColumns = keys?.filter(
                key =>
                    !columnMapping?.[key]?.languageType &&
                    ![columnTypes.RECORD_ID, columnTypes?.PATH_TAG]?.includes(columnMapping?.[key]?.type)
            );

            const newContentColumns = [
                ...otherColumns?.map(key => {
                    const column = columnMapping?.[key];
                    const id = column?.name?.split(' ')?.join('_');

                    return {
                        ...column,
                        publicId: id,
                        columnId: id,
                        id
                    };
                })
            ];

            setContentColumns(newContentColumns);

            if (keys?.length) {
                const newNormalColumns = [
                    ...keys
                        ?.filter(
                            key => ![columnTypes.RECORD_ID, columnTypes?.PATH_TAG]?.includes(columnMapping?.[key]?.type)
                        )
                        ?.map(key => {
                            const column = columnMapping?.[key];
                            const id = column?.name?.split(' ')?.join('_');

                            return {
                                ...column,
                                id: id,
                                publicId: id,
                                columnId: id,
                                type: columnTypes.MULTIPLE_LINES
                            };
                        })
                ];
                setNormalColumns(newNormalColumns);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [headers]);

    const handleCreateLocalizationGrid = React.useCallback(() => {
        setIsSubmitting(true);
        sendManualTrack({ type: `Add Localization Grid From Scratch` });
        let body = {
            name,
            type: 'Multiple',
            description: t('localization_grid_default_description'),
            totalRecord,
            targetLanguages: validSelectedTargetOptions.map(option => option?.value)
        };

        if (sources?.length === 1) {
            body = {
                ...body,
                type: LOCALIZATION_TYPES.SINGLE,
                sourceLanguage: sources?.[0]?.value
            };
        } else {
            body = {
                ...body,
                type: LOCALIZATION_TYPES.MULTIPLE,
                sourceLanguages: sources?.filter(source => !!source)?.map(source => source?.value)
            };
        }

        dispatch(
            gridActions.createLocalizationGrid({
                dbId,
                body: {
                    ...body,
                    extraInfoColumns: contentColumns,
                    enablePublicRecordId: checkboxRecordIdRef.current.checked,
                    recordIdentifierType: nonUnique
                        ? RECORD_IDENTIFIER_TYPE.pathTagRecordId
                        : RECORD_IDENTIFIER_TYPE.recordId,
                    globalUniquePublicRecordId: globalUnique
                },
                successCallback: newGrid => {
                    console.log('create localization grid success');
                    setIsSubmitting(false);

                    if (isRedirect) {
                        history.push(
                            `/projects/${workspaceId}/databases/${dbId}/grids/${newGrid.id}/branches/${newGrid.id}/views/${newGrid?.defaultAccessViewId}`
                        );
                        setTimeout(() => dispatch(gridUIActions.openNotiDependency()), 1000);
                    } else {
                        onGridCreated(newGrid);
                        onClose && onClose();
                    }
                },
                errorCallback: () => {
                    setIsSubmitting(false);
                    console.log('failed to create localization');
                }
            })
        );
    }, [
        t,
        dbId,
        dispatch,
        history,
        name,
        sources,
        workspaceId,
        validSelectedTargetOptions,
        contentColumns,
        isRedirect,
        onGridCreated,
        onClose,
        totalRecord,
        nonUnique,
        globalUnique
    ]);

    const handleCreateGrid = React.useCallback(() => {
        if (isTranslationOn) {
            handleCreateLocalizationGrid();
        } else {
            setIsSubmitting(true);
            dispatch(
                gridActions.createNormalGrid({
                    dbId,
                    grid: {
                        name,
                        customProperties: { color: getGridRandomColor() },
                        totalRecord
                    },
                    columns: normalColumns,
                    recordIdentifierType: nonUnique
                        ? RECORD_IDENTIFIER_TYPE.pathTagRecordId
                        : RECORD_IDENTIFIER_TYPE.recordId,
                    globalUniquePublicRecordId: globalUnique,
                    successCallback: newGrid => {
                        setIsSubmitting(false);
                        onClose();
                    },
                    errorCallback: () => {
                        setIsSubmitting(false);
                    }
                })
            );
        }
    }, [
        isTranslationOn,
        dbId,
        normalColumns,
        totalRecord,
        dispatch,
        onClose,
        name,
        handleCreateLocalizationGrid,
        nonUnique,
        globalUnique
    ]);

    const handleAddSource = React.useCallback(() => {
        setSources([...sources, null]);
    }, [sources]);

    const targetOptionChunks = React.useMemo(() => {
        return chunk(targetOptions, ITEMS_PER_ROW);
    }, [targetOptions]);

    const _rowRenderer = React.useCallback(
        ({ index, isScrolling, key, style }) => {
            const items = targetOptionChunks?.[index];
            return (
                <Grid style={{ ...style }} key={key} container alignItems="center" direction="row" wrap="nowrap">
                    {items?.map(option => {
                        const isSelected = selectedTargetOptions?.[option?.value];

                        if (option?.isDisabled) {
                            return (
                                <Grid key={option?.value} item xs={3}>
                                    <Grid container alignItems="center" justify="center">
                                        <Grid item className={classes.rowItem}>
                                            <ListSelect
                                                onClick={() => handleTargetSelect(option)}
                                                disabled={option?.isDisabled}
                                                disabledTitle={option?.disabledTitle}
                                                isSelected={isSelected}
                                                icon={option?.icon}
                                                name={option.value}
                                                moreIcon={option?.moreIcon}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            );
                        }
                        return (
                            <Tooltip title={option.tooltip} key={option.value}>
                                <Grid item xs={3}>
                                    <Grid container alignItems="center" justify="center">
                                        <Grid item className={classes.rowItem}>
                                            <ListSelect
                                                onClick={() => handleTargetSelect(option)}
                                                disabled={option?.isDisabled}
                                                disabledTitle={option?.disabledTitle}
                                                isSelected={isSelected}
                                                icon={option?.icon}
                                                name={option.value}
                                                moreIcon={option?.moreIcon}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Tooltip>
                        );
                    })}
                </Grid>
            );
        },
        [handleTargetSelect, targetOptionChunks, selectedTargetOptions, classes]
    );

    const _noRowsRenderer = React.useCallback(() => {
        return (
            <Grid
                container
                style={{ width: '100%', height: '100%' }}
                alignItems="center"
                justify="center"
                direction="column"
            >
                <Grid item>
                    <NoDiffCheck />
                </Grid>
                <Grid item>
                    <Typography variant="caption">{t('global_empty_language_msg')}</Typography>
                </Grid>
            </Grid>
        );
    }, [t]);

    const onContentColumnsChange = React.useCallback(columns => {
        setContentColumns(columns);
    }, []);

    const onNormalColumnsChange = React.useCallback(columns => {
        setNormalColumns(columns);
    }, []);

    return (
        <>
            <DialogTitle title={t('create_grid_from_scratch')} onClose={onClose} />
            <DialogContent>
                <Grid container className={classes.root} direction="column" spacing={4} wrap="nowrap">
                    <Grid item container direction="column" spacing={2}>
                        <Grid item>
                            <Typography variant="body1">{t('grid_name_input')}</Typography>
                        </Grid>
                        <Grid item>
                            <InputText
                                placeholder={t('localization_grid_name_input')}
                                name="projectName"
                                value={name}
                                error={!name}
                                // errorText={t('global_this_field_is_required')}
                                // disabled={isSubmitting}
                                onChange={e => setName(e.target.value)}
                                autoFocus={true}
                            />
                        </Grid>
                    </Grid>

                    <Grid item container alignItems="center" direction="row" spacing={2}>
                        <Grid item>
                            <Grid container direction="row" spacing={2} alignItems="center" wrap="nowrap">
                                <Grid item>
                                    <IOSSwitch
                                        checked={isTranslationOn}
                                        onChange={() => setTranslationOn(!isTranslationOn)}
                                        className={classes.switchIcon}
                                    />
                                </Grid>
                                <Grid item>
                                    <Typography variant="body1">{t('import_with_translation_option')}</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {isTranslationOn ? (
                        <Grid item>
                            <Grid container>
                                <Grid item container direction="column" className={classes.row} spacing={2}>
                                    <Grid item>
                                        <Grid container direction="row" alignItems="center" spacing={2}>
                                            <Grid item className={classes.numberStep}>
                                                {t('global_1')}
                                            </Grid>
                                            <Grid item>
                                                <Typography variant="body1">
                                                    {t('global_select_source_lang')}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item className={classes.fullWidth}>
                                        <Grid item className={classes.box}>
                                            <Grid container alignItems="center" direction="row" wrap="nowrap">
                                                {sources?.map((source, index) => {
                                                    return (
                                                        <>
                                                            <Grid
                                                                item
                                                                className={classes.w170}
                                                                key={source?.value || index}
                                                            >
                                                                <LDBasePortalVirtualize
                                                                    ddPlaceholder={t('global_languague_select')}
                                                                    menuPlaceholder={t('global_languague_search')}
                                                                    options={langOptions}
                                                                    onChange={option =>
                                                                        handleMultiSourceChange({ option, index })
                                                                    }
                                                                    defaultValue={source}
                                                                    isMulti={false}
                                                                    isCreatable={true}
                                                                    onCreateNew={() => handleMultiSourceRemove(index)}
                                                                    createText={t('global_delete')}
                                                                    createIcon={
                                                                        <DeleteSVG color={theme.palette.primary.main} />
                                                                    }
                                                                />
                                                            </Grid>
                                                            {index !== sources?.length && (
                                                                <Grid item className={classes.split}>
                                                                    <PathSVG />
                                                                </Grid>
                                                            )}
                                                        </>
                                                    );
                                                })}

                                                <Grid item>
                                                    <Grid
                                                        className={classes.addMoreSource}
                                                        container
                                                        alignItems="center"
                                                        direction="row"
                                                        justify="center"
                                                        onClick={handleAddSource}
                                                    >
                                                        <Grid item className={classes.dlFlex}>
                                                            <ColorAddSVG />
                                                        </Grid>
                                                        <Grid item>
                                                            <Typography variant="body2">
                                                                {t('localization_add_more_source')}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item container direction="column" className={classes.row} spacing={2}>
                                    <Grid item container alignItems="center" direction="row" justify="space-between">
                                        <Grid item>
                                            <Grid container direction="row" alignItems="center" spacing={2}>
                                                <Grid item className={classes.numberStep}>
                                                    {t('global_2')}
                                                </Grid>
                                                <Grid item>
                                                    <Typography variant="body1">
                                                        {t('global_select_target_lang')}
                                                    </Typography>
                                                </Grid>
                                                <Grid item>|</Grid>
                                                <Grid item>
                                                    <Typography variant="caption">
                                                        {t('localization_selected_langs_note', {
                                                            selectedLangs: validSelectedTargetOptions.length,
                                                            total: langOptions.length
                                                        })}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item>
                                            <Search
                                                className={classes.search}
                                                onChange={e => setFilterValue(e.target.value)}
                                                background={theme.colors.paleGrey}
                                                placeholder={t('localization_target_search')}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Grid item>
                                        <Grid item className={classes.targetList}>
                                            <AutoSizer>
                                                {({ width, height }) => {
                                                    return (
                                                        <List
                                                            className={classes.virtualizeList}
                                                            rowCount={Math.ceil(targetOptions?.length / ITEMS_PER_ROW)}
                                                            rowHeight={42}
                                                            width={width}
                                                            height={height}
                                                            noRowsRenderer={_noRowsRenderer}
                                                            rowRenderer={_rowRenderer}
                                                        />
                                                    );
                                                }}
                                            </AutoSizer>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid
                                    item
                                    container
                                    direction="row"
                                    className={classes.row}
                                    spacing={2}
                                    alignItems="center"
                                >
                                    <Grid item>
                                        <Grid
                                            container
                                            direction="column"
                                            spacing={4}
                                            justify="space-between"
                                            alignItems="center"
                                        >
                                            <Grid item>
                                                <Grid container direction="row" alignItems="center" spacing={2}>
                                                    <Grid item className={classes.numberStep}>
                                                        {t('global_3')}
                                                    </Grid>
                                                    <Grid item>
                                                        <Typography variant="body1">
                                                            {t('localization_mapping_more_label')}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item style={{ flex: 1 }}>
                                    <Grid container direction="column" spacing={2}>
                                        <NormalEmptySetup columns={contentColumns} onChange={onContentColumnsChange} />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid item>
                            <Grid container direction="column" spacing={2}>
                                <Grid item>
                                    <Typography variant="body1">Set up content columns</Typography>
                                </Grid>
                                <NormalEmptySetup
                                    columns={normalColumns}
                                    onChange={onNormalColumnsChange}
                                    tooltipDataType={1}
                                    tooltipColumnId={1}
                                />
                            </Grid>
                        </Grid>
                    )}
                    {isTranslationOn ? (
                        <Grid item>
                            <Grid
                                item
                                className={classes.row}
                                container
                                direction="row"
                                spacing={2}
                                alignItems="center"
                            >
                                <Grid item>
                                    <Grid container direction="row" alignItems="center" spacing={2}>
                                        <Grid item className={classes.numberStep}>
                                            {t('global_4')}
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="body1">{t('advanced_options')}</Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item container wrap="nowrap">
                                    <Grid item xs={4}>
                                        <Grid item container alignItems="center">
                                            <Grid item>
                                                <Checkbox
                                                    inputRef={checkboxRecordIdRef}
                                                    style={{ padding: 0, marginRight: 8 }}
                                                />
                                            </Grid>
                                            <Grid item style={{ marginRight: 8 }}>
                                                <Typography variant="body2">{t('enable_record_id_column')}</Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <AccessControl view={roleConst.SUPPORT_PATHTAG_UNIQUE_RECORDID}>
                                        <Grid item xs={4}>
                                            <Grid item container alignItems="center">
                                                <Grid item>
                                                    <Checkbox
                                                        checked={nonUnique}
                                                        onChange={handleNonUniqueChange}
                                                        style={{ padding: 0, marginRight: 8 }}
                                                    />
                                                </Grid>
                                                <Grid item style={{ marginRight: 8 }}>
                                                    <Typography variant="body2">
                                                        {t('allow_non_unique_record_ids')}
                                                    </Typography>
                                                </Grid>
                                                <Tooltip title="You can import non-unique record IDs if you map unique Path tags for each record">
                                                    <Grid item className={classes.displayFlex}>
                                                        <QuestionSVG />
                                                    </Grid>
                                                </Tooltip>
                                            </Grid>
                                        </Grid>
                                    </AccessControl>
                                    <AccessControl view={roleConst.SUPPORT_PATHTAG_UNIQUE_RECORDID}>
                                        <Grid item xs={4}>
                                            <Grid item container alignItems="center">
                                                <Grid item>
                                                    <Checkbox
                                                        checked={globalUnique}
                                                        onChange={handleGlobalUniqueChange}
                                                        style={{ padding: 0, marginRight: 8 }}
                                                    />
                                                </Grid>
                                                <Grid item style={{ marginRight: 8 }}>
                                                    <Typography variant="body2">
                                                        {t('global_unique_record_ids')}
                                                    </Typography>
                                                </Grid>
                                                <Tooltip title="Record IDs will be auto-generated and cannot be edited to ensure the uniqueness">
                                                    <Grid item className={classes.displayFlex}>
                                                        <QuestionSVG />
                                                    </Grid>
                                                </Tooltip>
                                            </Grid>
                                        </Grid>
                                    </AccessControl>
                                </Grid>
                            </Grid>
                        </Grid>
                    ) : (
                        <AccessControl view={roleConst.SUPPORT_PATHTAG_UNIQUE_RECORDID}>
                            <Grid item>
                                <Grid item container direction="column" style={{ marginTop: 14 }}>
                                    <Grid item style={{ marginBottom: 8 }}>
                                        <Typography variant="body1">{t('advanced_options')}</Typography>
                                    </Grid>
                                    <Grid item container wrap="nowrap">
                                        <Grid item xs={4} container alignItems="center">
                                            <Grid item>
                                                <Checkbox
                                                    checked={nonUnique}
                                                    onChange={handleNonUniqueChange}
                                                    style={{ padding: 0, marginRight: 8 }}
                                                />
                                            </Grid>
                                            <Grid item style={{ marginRight: 8 }}>
                                                <Typography variant="body2">
                                                    {t('allow_non_unique_record_ids')}
                                                </Typography>
                                            </Grid>
                                            <Tooltip title="You can import non-unique record IDs if you map unique Path tags for each record">
                                                <Grid item className={classes.displayFlex}>
                                                    <QuestionSVG />
                                                </Grid>
                                            </Tooltip>
                                        </Grid>
                                        <Grid item xs={4} container alignItems="center">
                                            <Grid item>
                                                <Checkbox
                                                    checked={globalUnique}
                                                    onChange={handleGlobalUniqueChange}
                                                    style={{ padding: 0, marginRight: 8 }}
                                                />
                                            </Grid>
                                            <Grid item style={{ marginRight: 8 }}>
                                                <Typography variant="body2">{t('global_unique_record_ids')}</Typography>
                                            </Grid>
                                            <Tooltip title="Record IDs will be auto-generated and cannot be edited to ensure the uniqueness">
                                                <Grid item className={classes.displayFlex}>
                                                    <QuestionSVG />
                                                </Grid>
                                            </Tooltip>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </AccessControl>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Grid item container justify="flex-end" spacing={2} alignItems="center">
                    <Grid item>
                        <ButtonBase onClick={onClose} variant="outlined" width={120}>
                            {t('global_cancel')}
                        </ButtonBase>
                    </Grid>
                    <Grid item>
                        <div className={classes.btnSubmit}>
                            <ButtonBase
                                onClick={handleCreateGrid}
                                disabled={!isCreateValid || isSubmitting}
                                variant="contained"
                                color="primary"
                                width={120}
                            >
                                {t('global_create')}
                            </ButtonBase>
                            {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                        </div>
                    </Grid>
                </Grid>
            </DialogActions>
        </>
    );
}

export default React.memo(LocalizationEmptySetup);
